import { useNavigate } from 'react-router-dom'
import Robot3D from './Robot3D'
import type { Lang } from '@/hooks/useLang'

interface HeroSectionProps {
  lang: Lang
}

const COPY = {
  fr: {
    tag: 'Projet : Humanoïde Alpha',
    title: ['Concevez le', 'futur.'],
    subtitle: (
      <>
        Définissez le rôle, les capacités et le niveau d'autonomie de votre futur robot humanoïde.{' '}
        <span className="text-primary italic">L'ingénierie de pointe à portée de main.</span>
      </>
    ),
    cta: 'Concevoir mon Robot',
    ctaSecondary: 'Voir des exemples',
    stat1Label: 'Taux de Sync',
    stat2Label: 'Latence Réponse',
  },
  en: {
    tag: 'Project: Humanoid Alpha',
    title: ['Design the', 'future.'],
    subtitle: (
      <>
        Define the role, capabilities, and autonomy level of your future humanoid robot.{' '}
        <span className="text-primary italic">Cutting-edge engineering at your fingertips.</span>
      </>
    ),
    cta: 'Design my Robot',
    ctaSecondary: 'See examples',
    stat1Label: 'Sync Rate',
    stat2Label: 'Response Latency',
  },
}

export default function HeroSection({ lang }: HeroSectionProps) {
  const navigate = useNavigate()
  const t = COPY[lang]

  return (
    <section
      id="protocols"
      className="relative min-h-[795px] flex flex-col md:flex-row items-center justify-between px-8 md:px-16 overflow-hidden"
    >
      {/* Left — narrative */}
      <div className="relative z-10 max-w-2xl pt-12 md:pt-0">
        {/* Tag */}
        <div className="mb-6 flex items-center gap-4">
          <div className="h-px w-12 bg-primary-container" />
          <span className="label-technical text-secondary">{t.tag}</span>
        </div>

        {/* Headline */}
        <h1 className="font-headline text-5xl md:text-7xl font-bold leading-none tracking-tight text-on-surface mb-8">
          {t.title[0]}{' '}
          <span className="text-primary-container">{t.title[1]}</span>
        </h1>

        {/* Subtitle */}
        <p className="font-body text-xl md:text-2xl text-on-surface-variant font-light leading-relaxed mb-12 max-w-lg">
          {t.subtitle}
        </p>

        {/* CTAs */}
        <div className="flex flex-wrap gap-6 items-center">
          <button
            onClick={() => void navigate('/configurator')}
            className="btn-primary text-lg"
          >
            {t.cta}
          </button>
          <button
            onClick={() => {
              document.getElementById('preview')?.scrollIntoView({ behavior: 'smooth' })
            }}
            className="btn-secondary text-lg"
          >
            {t.ctaSecondary}
          </button>
        </div>

        {/* Stats */}
        <div className="mt-20 grid grid-cols-2 gap-12 border-t border-outline-variant/10 pt-10">
          <div>
            <div className="font-headline text-3xl text-primary-container">99.8%</div>
            <div className="label-technical mt-1">{t.stat1Label}</div>
          </div>
          <div>
            <div className="font-headline text-3xl text-secondary">0.02ms</div>
            <div className="label-technical mt-1">{t.stat2Label}</div>
          </div>
        </div>
      </div>

      {/* Right — Robot 3D */}
      <div className="relative w-full md:w-1/2 h-[560px] md:h-[760px] mt-12 md:mt-0 flex items-center justify-center">
        <Robot3D />
      </div>
    </section>
  )
}
